import log from '../../services/Logger';
/**
 * Half-open index range [from, to) of items in the result set.
 *
 * Immutable: all operations return new ranges.
 */
export class Range {
    from;
    to;
    size;
    constructor(from, to) {
        if (from > to) {
            throw 'invalid range';
        }
        this.from = from;
        this.to = to;
        this.size = to - from;
    }
    contains(other) {
        return this.from <= other.from && this.to >= other.to;
    }
    intersectsOrTouches(other) {
        return (this.to >= other.from && this.to <= other.to && this.from <= other.from)
            || (this.from >= other.from && this.from <= other.to && this.to >= other.to);
    }
    combine(other) {
        return new Range(Math.min(this.from, other.from), Math.max(this.to, other.to));
    }
    next() {
        return new Range(this.to, this.to + this.size);
    }
    prev() {
        return new Range(this.from - this.size, this.from);
    }
}
/**
 * The currently loaded window of items: the items themselves plus the range
 * they cover in the full result set.
 *
 * Immutable: `combine()` returns a new slice. Items are kept as plain objects,
 * so a slice can be stored in a `shallowRef` without making every item
 * reactive.
 */
export class DataItemSlice {
    range;
    items;
    constructor(range, items) {
        this.range = range;
        this.items = items;
    }
    static empty() {
        return new DataItemSlice(new Range(0, 0), []);
    }
    /**
     * Combines this slice with another and returns the result.
     * Note: it is assumed that the items in `other` are sorted the
     * same way as this items.
     *
     * Items already present are replaced in place (never duplicated). When
     * prepending, items are trimmed from the end so that at most two page
     * sizes remain after the new page; when appending, items are trimmed from
     * the beginning so that at most three page sizes remain before the end of
     * the new page.
     */
    combine(other) {
        if (!this.range.intersectsOrTouches(other.range)) {
            throw 'can only combine slices that intersect or touch';
        }
        const items = [...this.items];
        let from = this.range.from;
        let to = this.range.to;
        // replace items
        const replacedItemIds = [];
        for (let i = 0; i < items.length; i++) {
            const item = other.items.find((item) => item.id === items[i].id);
            if (item) {
                items[i] = item;
                log(`replaced item ${item.id} in current slice`);
                replacedItemIds.push(item.id);
            }
        }
        // prepend items
        if (from > other.range.from) {
            for (let i = from - other.range.from; i > 0; i--) {
                const item = other.items[i - 1];
                if (replacedItemIds.indexOf(item.id) === -1) {
                    items.unshift(item);
                }
            }
            // remove from the end
            const toRemove = [other.range.to + other.range.size, to];
            if (toRemove[0] < toRemove[1]) {
                log('removing items from the end: ' + toRemove);
                for (let i = toRemove[1]; i > toRemove[0]; i--) {
                    items.pop();
                    to -= 1;
                }
            }
        }
        // append items
        const effectiveRangeTo = Math.min(from + items.length, to);
        if (effectiveRangeTo < other.range.to) {
            // the start index is negative if this slice was a short (last)
            //   page; the other slice is then empty or beyond the total
            for (let i = Math.max(0, effectiveRangeTo - other.range.from); i < other.items.length; i++) {
                const item = other.items[i];
                if (replacedItemIds.indexOf(item.id) === -1) {
                    items.push(item);
                }
            }
            // remove from the beginning
            const toRemove = [from, other.range.to - (other.range.size * 3)];
            if (toRemove[0] < toRemove[1]) {
                log('removing items from the beginning: ' + toRemove);
                for (let i = toRemove[0]; i < toRemove[1]; i++) {
                    items.shift();
                    from += 1;
                }
            }
        }
        const range = new Range(from, to).combine(other.range);
        log('new range: ' + [range.from, range.to]);
        return new DataItemSlice(range, items);
    }
}
